import { toast } from "sonner";

/**
 * Global member-session expiry handling (shared axios interceptor).
 *
 * Fires ONLY when:
 *   • the response is a genuine 401, AND
 *   • the request carried a Bearer token (an authenticated member call), AND
 *   • the endpoint is not an auth/admin/auth-check endpoint (no loops).
 *
 * Behavior: one deduped toast, one sign-out, and — only when the user is
 * standing on a member-only surface — one redirect to sign-in that
 * preserves the destination. Public pages are never redirected.
 */

const EXEMPT = [
  /\/auth\//,            // login, signup, logout, reset, session exchange
  /\/admin/,             // admin auth uses separate credentials
  // ALL personal member-surface data (/members/me and every nested desk/
  // workbench/wall/notes call). useMemberAuth is the single authority on
  // session validity via /members/me — a stray 401 from a workspace
  // sub-call must NEVER cascade a global sign-out + login redirect
  // (this was bouncing members straight out of /members).
  /\/members\/me(\/|$)/,
  /\/members\/subscribe$/,
  /\/members\/logout$/,
];

const MEMBER_SURFACES = [/^\/inbox/, /^\/members/];

let _signOut = null;
export const registerSessionSignOut = (fn) => { _signOut = fn; };

let _handling = false;

export const attachSessionExpiryInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.response.use(
    (r) => r,
    (error) => {
      try {
        const status = error?.response?.status;
        const cfg = error?.config || {};
        const url = String(cfg.url || "");
        const auth = cfg.headers?.Authorization || cfg.headers?.authorization || "";
        const hadBearer = /^bearer /i.test(String(auth));
        const exempt = EXEMPT.some((re) => re.test(url));
        if (status === 401 && hadBearer && !exempt && !_handling) {
          _handling = true;
          toast.error("Session expired — please sign in again.",
            { id: "member-session-expired" });
          try { _signOut?.(); } catch { /* noop */ }
          const path = window.location.pathname;
          if (MEMBER_SURFACES.some((re) => re.test(path))) {
            // Single redirect; concurrent 401s are already gated above.
            setTimeout(() => {
              window.location.assign(
                `/auth/login?next=${encodeURIComponent(path + window.location.search)}`);
            }, 700);
          } else {
            // Public page: signed out quietly, no redirect. Re-arm later.
            setTimeout(() => { _handling = false; }, 5000);
          }
        }
      } catch { /* interceptor must never break the error chain */ }
      return Promise.reject(error);
    });
  return axiosInstance;
};
